import 'moment-timezone'

import {
  CampaignCapacityDTO,
  CampaignPostDTO,
  ContentInventoryDTO,
  ContentKind,
  MediaOrigin,
  SocialCampaignDTO,
  VisualKeyDTO
} from 'models/socialCampaign'

import { campaignDayOf, dayLocaleOf } from './campaignStatus'
import moment from 'moment'

/**
 * Section 1 of a draft campaign, "Plan & content" (ISSUE-3622, prototype v3 `contentSection`): the plan sentence,
 * what the content can carry, and the kinds of content the plan draws on. The backend computes the capacity, the
 * posts using each kind and the posts a missing kind would add (ISSUE-3984); the content inventory gives the counts.
 */

/** The plan sentence's choices, exactly the backend's (`CampaignPlan.POSTS_CHOICES` / `WEEKS_CHOICES`). */
export const PLAN_POSTS_CHOICES: readonly number[] = [4, 5, 6, 7, 8, 9, 10, 12]
export const PLAN_WEEKS_CHOICES: readonly number[] = [1, 2, 3, 4, 6]

/** "a video, virtual staging or a floor plan": alternatives joined the way the UI language joins them. */
export const joinAlternatives = (items: string[], language: string) =>
  new Intl.ListFormat(dayLocaleOf(language), { type: 'disjunction' }).format(items)

/** A select's choices with the saved value in its place among them, when it is not one of them. */
export const withChoice = (choices: readonly number[], value: number): readonly number[] =>
  choices.includes(value) ? choices : [...choices, value].sort((a, b) => a - b)

/** The kinds in the order the section lists them (the backend's enum order). */
export const CONTENT_KIND_ORDER: readonly ContentKind[] = [
  ContentKind.PHOTOS,
  ContentKind.VIDEOS,
  ContentKind.VIRTUAL_STAGING,
  ContentKind.FLOOR_PLANS,
  ContentKind.DRONE,
]

/** Under this many photos, the locked Photos card offers "+ Add more" (prototype v3: `n < 6`). */
export const ADD_MORE_PHOTOS_BELOW = 6

/** Whether the plan draws on a kind; photos are always in, a kind without a setting is in. */
export const isContentKindIncluded = (campaign: Pick<SocialCampaignDTO, 'contentKinds'>, kind: ContentKind) =>
  kind === ContentKind.PHOTOS || (campaign.contentKinds.find((setting) => setting.kind === kind)?.included ?? true)

/** Posts a week, to one decimal ("≈ 2.3 a week"). */
export const planPace = (posts: number, weeks: number) => Math.round((posts / weeks) * 10) / 10

/** A locale Intl formats numbers in; English for a language it cannot take ('', 'de_DE'). */
const numberLocaleOf = (language: string | undefined) => {
  if (!language) return 'en-GB'
  try {
    return Intl.NumberFormat.supportedLocalesOf(language)[0] ?? 'en-GB'
  } catch {
    return 'en-GB'
  }
}

/** The pace in the UI language ("2.3", "2,3"). Runs in renders, so it never throws (as `formatCampaignDay`). */
export const formatPlanPace = (pace: number, language: string | undefined) =>
  new Intl.NumberFormat(numberLocaleOf(language), { maximumFractionDigits: 1 }).format(pace)

/**
 * The cadence strip: one entry per day of the plan from its start (`weeks` × 7), true on a day a post goes out in
 * the campaign's timezone. Posts waiting for an order mark their day too.
 */
export function cadenceDays(
  plan: Pick<SocialCampaignDTO['plan'], 'start' | 'weeks'>,
  posts: Pick<CampaignPostDTO, 'publishAt'>[],
  timezone: string
): boolean[] {
  const postDays = new Set(posts.map((post) => campaignDayOf(post.publishAt, timezone)))
  const start = moment(plan.start, 'YYYY-MM-DD')
  return Array.from({ length: plan.weeks * 7 }, (_, index) => postDays.has(start.clone().add(index, 'days').format('YYYY-MM-DD')))
}

/** What the line under the plan sentence says (prototype v3 capacity line). */
export type CapacityLine =
  /** "Enough content for 10 different posts, your plan uses 7" */
  | { tone: 'ok', differentPosts: number, posts: number }
  /** "Your content covers 6 different posts. From post 7, topics start repeating. Add … for more variety" */
  | { tone: 'warn', differentPosts: number, posts: number, missing: ContentKind[] }

/**
 * Enough when the content tells at least as many different posts as the plan asks for. The capacity does not depend
 * on the number of posts, so the plan sentence's choice compares at once, before the re-plan answers. The warning
 * names the kinds the property does not have that would open a post.
 */
export function capacityLine(capacity: CampaignCapacityDTO, posts: number): CapacityLine {
  const { differentPosts } = capacity
  if (differentPosts >= posts) return { tone: 'ok', differentPosts, posts }
  const missing = CONTENT_KIND_ORDER.filter((kind) => {
    const entry = capacity.kinds.find((item) => item.kind === kind)
    return !!entry && !entry.available && entry.unlocks.length > 0
  })
  return { tone: 'warn', differentPosts, posts, missing }
}

const sameVisual = (a: VisualKeyDTO, b: VisualKeyDTO) =>
  a.assignmentPublicId === b.assignmentPublicId && a.filename === b.filename && a.visualType === b.visualType

/** One kind of content as its card shows it. */
export interface ContentKindCard {
  kind: ContentKind
  /** The property has at least one visual of it ("In your gallery") */
  available: boolean
  /** The plan draws on it; photos always do */
  included: boolean
  /** Photos: always included, no checkbox */
  locked: boolean
  /** Visuals of the kind in the gallery; null until the inventory is read, or when it has none of an available kind */
  count: number | null
  /** Those the client did not leave out (ISSUE-3651); null until the inventory is read */
  kept: number | null
  /** Those made with AI; null until the inventory is read */
  madeWithAi: number | null
  /** Posts of the plan showing a visual of the kind, posts waiting for an order left out */
  postsUsing: number
  /** Titles of the posts the kind would add once the property has it; empty when available */
  unlocks: string[]
}

/**
 * The cards of the section, in kind order, from the capacity (availability, use, unlocks), the inventory (counts,
 * kept, AI) and the inclusion as shown (the client's last choice, saved or on its way).
 */
export function contentKindCards(
  capacity: CampaignCapacityDTO,
  inventory: ContentInventoryDTO | undefined,
  excludedVisuals: VisualKeyDTO[],
  isIncluded: (kind: ContentKind) => boolean
): ContentKindCard[] {
  return CONTENT_KIND_ORDER.map((kind) => {
    const entry = capacity.kinds.find((item) => item.kind === kind)
    const stock = inventory?.kinds.find((item) => item.kind === kind)
    const leftOut = stock ? stock.items.filter((item) => excludedVisuals.some((visual) => sameVisual(item, visual))).length : 0
    const available = entry?.available ?? false
    // the inventory is the viewer's (seat-hidden orders left out) and can predate the capacity: none of a kind the
    // workspace has means its counts are unknown, not zero
    const counted = !!inventory && !(available && !stock?.count)
    return {
      kind,
      available,
      included: isIncluded(kind),
      locked: kind === ContentKind.PHOTOS,
      count: counted ? stock?.count ?? 0 : null,
      kept: counted ? Math.max(0, (stock?.count ?? 0) - leftOut) : null,
      madeWithAi: counted ? stock?.items.filter((item) => item.origin === MediaOrigin.AI).length ?? 0 : null,
      postsUsing: entry?.postsUsing ?? 0,
      unlocks: entry?.unlocks ?? [],
    }
  })
}
